import type { Episode, Podcast } from '@/types';
import { usePlayer } from '@/context/PlayerContext';

interface EpisodeCardProps {
  episode: Episode;
  podcast: Podcast;
}

function formatDuration(seconds: number): string {
  if (!seconds || seconds <= 0) return '';
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }
  return `${minutes} min`;
}

function formatDate(timestamp: number): string {
  if (!timestamp) return '';
  const date = new Date(timestamp * 1000);
  return date.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

export function EpisodeCard({ episode, podcast }: EpisodeCardProps) {
  const { state, play, pause, resume } = usePlayer();
  const isCurrentEpisode = state.episode?.id === episode.id;
  const isPlaying = isCurrentEpisode && state.isPlaying;
  const hasError = isCurrentEpisode && state.error;

  const handlePlayClick = () => {
    if (isCurrentEpisode) {
      if (isPlaying) {
        pause();
      } else {
        resume();
      }
    } else {
      play(episode, podcast);
    }
  };

  return (
    <div className={`glass-panel transition-all duration-200 ${isCurrentEpisode ? 'border-primary/50 bg-primary/5' : ''}`}>
      <div className="flex gap-4">
        <button
          onClick={handlePlayClick}
          className={`flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center transition-all ${
            hasError
              ? 'bg-red-500/20 text-red-400 hover:bg-red-500/30'
              : isPlaying
              ? 'bg-primary text-white'
              : 'bg-white/10 text-white hover:bg-primary hover:text-white'
          }`}
          title={hasError ? state.error || 'Playback error' : undefined}
        >
          {hasError ? (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          ) : state.isLoading && isCurrentEpisode ? (
            <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
          ) : isPlaying ? (
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
              <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" />
            </svg>
          ) : (
            <svg className="w-5 h-5 ml-0.5" fill="currentColor" viewBox="0 0 24 24">
              <path d="M8 5v14l11-7z" />
            </svg>
          )}
        </button>
        <div className="flex-1 min-w-0">
          <h4 className="font-medium text-white line-clamp-2 mb-1">
            {episode.title}
          </h4>
          <div className="flex items-center gap-3 text-sm text-white/50">
            {episode.datePublished && (
              <span>{formatDate(episode.datePublished)}</span>
            )}
            {episode.duration > 0 && (
              <>
                <span>•</span>
                <span>{formatDuration(episode.duration)}</span>
              </>
            )}
          </div>
          {episode.description && (
            <p
              className="text-sm text-white/60 mt-2 line-clamp-2"
              dangerouslySetInnerHTML={{
                __html: episode.description.replace(/<[^>]*>/g, ''),
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
}
